import { logger } from '../utils/logger';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { 
  Upload, 
  Image as ImageIcon, 
  Trash2, 
  Sparkles, 
  Loader2,
  ZoomIn,
  FileImage,
  PenTool,
  Columns,
  Ruler
} from 'lucide-react';
import { toast } from 'sonner';
import ImageAnnotator from './ImageAnnotator';
import ImageComparison from './ImageComparison';

import { BACKEND_URL } from '../config/api';

const ImageUploader = ({ patientId, examId, onImagesChange }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageBlobs, setImageBlobs] = useState({});
  const [analysisResult, setAnalysisResult] = useState(null);
  const [uploadLabel, setUploadLabel] = useState('');
  const [analysisType, setAnalysisType] = useState('general');
  const [annotatingImage, setAnnotatingImage] = useState(null);
  const [savingAnnotations, setSavingAnnotations] = useState(false);
  const [showComparison, setShowComparison] = useState(false);

  const fetchImages = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/images/patient/${patientId}`,
        { withCredentials: true }
      );
      setImages(data);
      onImagesChange?.(data);
      
      // Fetch image blobs for display
      for (const img of data) {
        if (!imageBlobs[img.id]) {
          fetchImageBlob(img.id);
        }
      }
    } catch (error) {
      logger.error('Error fetching images:', error);
    } finally {
      setLoading(false);
    }
  }, [patientId, onImagesChange, imageBlobs]);

  useEffect(() => {
    fetchImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  const fetchImageBlob = async (imageId) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/images/download/${imageId}`,
        { withCredentials: true, responseType: 'blob' }
      );
      const blobUrl = URL.createObjectURL(response.data);
      setImageBlobs(prev => ({ ...prev, [imageId]: blobUrl }));
    } catch (error) {
      logger.error('Error fetching image blob:', error);
    }
  };

  const handleUpload = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    const uploadPromises = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const formData = new FormData();
      formData.append('file', file);
      
      const label = uploadLabel || file.name;
      const params = new URLSearchParams({
        label: files.length > 1 ? `${label} (${i + 1})` : label,
        patient_id: patientId,
        ...(examId && { exam_id: examId })
      });

      uploadPromises.push(
        axios.post(
          `${BACKEND_URL}/api/images/upload?${params.toString()}`,
          formData,
          { 
            withCredentials: true,
            headers: { 'Content-Type': 'multipart/form-data' }
          }
        )
      );
    }

    try {
      await Promise.all(uploadPromises);
      toast.success(`${files.length} image(s) uploaded successfully`);
      setUploadLabel('');
      fetchImages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload images');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleDelete = async (imageId) => {
    try {
      await axios.delete(
        `${BACKEND_URL}/api/images/${imageId}`,
        { withCredentials: true }
      );
      toast.success('Image deleted');
      setImages(images.filter(img => img.id !== imageId));
      if (imageBlobs[imageId]) {
        URL.revokeObjectURL(imageBlobs[imageId]);
        setImageBlobs(prev => {
          const newBlobs = { ...prev };
          delete newBlobs[imageId];
          return newBlobs;
        });
      }
    } catch (error) {
      toast.error('Failed to delete image');
    }
  };

  const handleAnalyze = async (selectedIds = null) => {
    const idsToAnalyze = selectedIds || images.map(img => img.id);
    if (idsToAnalyze.length === 0) {
      toast.error('No images to analyze');
      return;
    }

    // Gather annotations for the images being analyzed
    const imageAnnotations = images
      .filter(img => idsToAnalyze.includes(img.id) && img.annotations?.length > 0)
      .map(img => ({
        image_id: img.id,
        label: img.label,
        annotations: img.annotations
      }));

    setAnalyzing(true);
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/images/analyze`,
        {
          image_ids: idsToAnalyze,
          exam_id: examId,
          analysis_type: analysisType,
          annotations: imageAnnotations
        },
        { withCredentials: true }
      );
      setAnalysisResult(data);
      toast.success('AI analysis complete');
      fetchImages(); // Refresh to get updated analysis
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSaveAnnotations = async (annotations) => {
    if (!annotatingImage) return;
    
    setSavingAnnotations(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/images/${annotatingImage.id}/annotations`,
        { annotations },
        { withCredentials: true }
      );
      toast.success(`Saved ${annotations.length} annotation(s)`);
      
      // Update local state
      setImages(prev => prev.map(img => 
        img.id === annotatingImage.id 
          ? { ...img, annotations } 
          : img
      ));
      
      setAnnotatingImage(null);
    } catch (error) {
      toast.error('Failed to save annotations');
    } finally {
      setSavingAnnotations(false);
    }
  };

  // Cleanup blob URLs on unmount
  useEffect(() => {
    const blobs = imageBlobs;
    return () => {
      Object.values(blobs).forEach(url => URL.revokeObjectURL(url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const analysisTypes = [
    { value: 'general', label: 'General Analysis' },
    { value: 'mri_brain', label: 'MRI Brain' },
    { value: 'mri_spine', label: 'MRI Spine' },
    { value: 'ct_head', label: 'CT Head' },
    { value: 'ct_spine', label: 'CT Spine' },
  ];

  return (
    <Card className="border-[#E5E4E0] bg-white" data-testid="image-uploader">
      <CardHeader>
        <CardTitle className="text-lg font-['Work_Sans'] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileImage className="w-5 h-5 text-[#4B6651]" />
            Medical Images
          </div>
          <div className="flex items-center gap-2">
            {images.length >= 2 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowComparison(true)}
                className="border-[#E5E4E0] text-xs"
                data-testid="compare-images-btn"
              >
                <Columns className="w-3 h-3 mr-1" />
                Compare
              </Button>
            )}
            <span className="text-sm font-normal text-[#5C5F5C]">
              {images.length} image{images.length !== 1 ? 's' : ''}
            </span>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Upload Section */}
        <div className="p-4 border-2 border-dashed border-[#E5E4E0] rounded-lg hover:border-[#4B6651]/50 transition-colors">
          <div className="space-y-3">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1">
                <Label htmlFor="imageLabel" className="text-sm text-[#5C5F5C] mb-1 block">
                  Image Label (optional)
                </Label>
                <Input
                  id="imageLabel"
                  value={uploadLabel}
                  onChange={(e) => setUploadLabel(e.target.value)}
                  placeholder="e.g., MRI T2 Sagittal"
                  className="bg-[#F8F7F4] border-[#E5E4E0]"
                  data-testid="image-label-input"
                />
              </div>
              <div className="flex items-end">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    multiple
                    accept=".jpg,.jpeg,.png,.webp,.dcm,.dicom"
                    onChange={handleUpload}
                    className="hidden"
                    data-testid="image-file-input"
                  />
                  <Button
                    type="button"
                    disabled={uploading}
                    className="bg-[#4B6651] hover:bg-[#3E5543] text-white"
                    asChild
                  >
                    <span>
                      {uploading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4 mr-2" />
                      )}
                      Upload Images
                    </span>
                  </Button>
                </label>
              </div>
            </div>
            <p className="text-xs text-[#5C5F5C]">
              Supported formats: JPEG, PNG, WebP, DICOM (.dcm)
            </p>
          </div>
        </div>

        {/* Images Grid */}
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-[#4B6651]" />
          </div>
        ) : images.length > 0 ? (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {images.map((image) => (
                <div 
                  key={image.id} 
                  className="relative group rounded-lg overflow-hidden border border-[#E5E4E0] bg-[#F8F7F4]"
                  data-testid={`image-${image.id}`}
                >
                  {imageBlobs[image.id] ? (
                    <img
                      src={imageBlobs[image.id]}
                      alt={image.label}
                      className="w-full h-32 object-cover"
                    />
                  ) : (
                    <div className="w-full h-32 flex items-center justify-center">
                      <Loader2 className="w-6 h-6 animate-spin text-[#5C5F5C]" />
                    </div>
                  )}
                  
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="text-white hover:bg-white/20"
                          onClick={() => setSelectedImage(image)}
                        >
                          <ZoomIn className="w-4 h-4" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-4xl">
                        <DialogHeader>
                          <DialogTitle>{image.label}</DialogTitle>
                        </DialogHeader>
                        <div className="mt-4">
                          {imageBlobs[image.id] && (
                            <img
                              src={imageBlobs[image.id]}
                              alt={image.label}
                              className="w-full max-h-[70vh] object-contain rounded-lg"
                            />
                          )}
                          {image.annotations?.length > 0 && (
                            <div className="mt-4 p-3 bg-[#C06144]/10 rounded-lg">
                              <p className="text-xs text-[#C06144] uppercase tracking-wider font-semibold mb-2 flex items-center gap-1">
                                <PenTool className="w-3 h-3" />
                                Annotations ({image.annotations.length})
                              </p>
                              <div className="text-sm text-[#2A2C2A] space-y-1">
                                {image.annotations.map((ann, idx) => (
                                  <div key={ann.id || idx} className="flex items-center gap-2">
                                    <span className="w-3 h-3 rounded-full" style={{ backgroundColor: ann.color }} />
                                    <span>{ann.description || ann.label || `Region ${idx + 1}`}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {image.dicom_metadata && (
                            <div className="mt-4 p-3 bg-[#F8F7F4] rounded-lg">
                              <p className="text-xs text-[#5C5F5C] uppercase tracking-wider font-semibold mb-2">
                                DICOM Metadata
                              </p>
                              <div className="grid grid-cols-2 gap-2 text-sm">
                                <div>
                                  <span className="text-[#5C5F5C]">Modality:</span>{' '}
                                  <span className="text-[#2A2C2A]">{image.dicom_metadata.modality}</span>
                                </div>
                                {image.dicom_metadata.series_description && (
                                  <div>
                                    <span className="text-[#5C5F5C]">Series:</span>{' '}
                                    <span className="text-[#2A2C2A]">{image.dicom_metadata.series_description}</span>
                                  </div>
                                )}
                                <div>
                                  <span className="text-[#5C5F5C]">Dimensions:</span>{' '}
                                  <span className="text-[#2A2C2A]">{image.dicom_metadata.columns}x{image.dicom_metadata.rows}</span>
                                </div>
                              </div>
                            </div>
                          )}
                          {image.ai_analysis && (
                            <div className="mt-4 p-3 bg-[#4B6651]/10 rounded-lg">
                              <p className="text-xs text-[#4B6651] uppercase tracking-wider font-semibold mb-2 flex items-center gap-1">
                                <Sparkles className="w-3 h-3" />
                                AI Analysis
                              </p>
                              <div className="text-sm text-[#2A2C2A] whitespace-pre-wrap">
                                {image.ai_analysis.analysis}
                              </div>
                            </div>
                          )}
                        </div>
                      </DialogContent>
                    </Dialog>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      className="text-white hover:bg-white/20"
                      onClick={() => setAnnotatingImage(image)}
                      title="Annotate"
                      data-testid={`annotate-${image.id}`}
                    >
                      <PenTool className="w-4 h-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      className="text-white hover:bg-red-500/50"
                      onClick={() => handleDelete(image.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  {/* Label */}
                  <div className="absolute bottom-0 left-0 right-0 bg-black/70 px-2 py-1">
                    <p className="text-xs text-white truncate">{image.label}</p>
                  </div>
                  
                  {/* Indicators */}
                  <div className="absolute top-1 right-1 flex gap-1">
                    {image.annotations?.length > 0 && (
                      <div className="w-5 h-5 bg-[#8B5CF6] rounded-full flex items-center justify-center" title={`${image.annotations.length} annotation(s)`}>
                        <PenTool className="w-3 h-3 text-white" />
                      </div>
                    )}
                    {image.ai_analysis && (
                      <div className="w-5 h-5 bg-[#C06144] rounded-full flex items-center justify-center" title="AI analyzed">
                        <Sparkles className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Analysis Section */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-[#E5E4E0]">
              <div className="flex-1">
                <Label className="text-sm text-[#5C5F5C] mb-1 block">Analysis Type</Label>
                <Select value={analysisType} onValueChange={setAnalysisType}>
                  <SelectTrigger className="bg-[#F8F7F4] border-[#E5E4E0]" data-testid="analysis-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {analysisTypes.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button
                  onClick={() => handleAnalyze()}
                  disabled={analyzing || images.length === 0}
                  className="bg-[#C06144] hover:bg-[#C06144]/90 text-white"
                  data-testid="analyze-images-btn"
                >
                  {analyzing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-2" />
                  )}
                  Analyze All Images
                </Button>
              </div>
            </div>

            {/* Annotation tip */}
            {images.some(img => !img.annotations?.length) && (
              <div className="flex items-center gap-2 p-3 bg-[#8B5CF6]/10 rounded-lg text-sm">
                <PenTool className="w-4 h-4 text-[#8B5CF6]" />
                <span className="text-[#5C5F5C]">
                  <strong className="text-[#2A2C2A]">Tip:</strong> Hover over an image and click the pen icon to annotate regions of interest for more targeted AI analysis.
                </span>
              </div>
            )}

            {/* Analysis Result */}
            {analysisResult && (
              <div className="p-4 bg-[#4B6651]/10 rounded-lg border border-[#4B6651]/30" data-testid="analysis-result">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-5 h-5 text-[#C06144]" />
                  <h4 className="font-semibold text-[#2A2C2A] font-['Work_Sans']">AI Image Analysis</h4>
                  <span className="ml-auto text-xs text-[#5C5F5C]">
                    {new Date(analysisResult.created_at).toLocaleString()}
                  </span>
                </div>
                <div className="text-sm text-[#2A2C2A] whitespace-pre-wrap leading-relaxed">
                  {analysisResult.ai_analysis}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-8">
            <ImageIcon className="w-12 h-12 text-[#E5E4E0] mx-auto mb-3" />
            <p className="text-[#5C5F5C]">No images uploaded yet</p>
            <p className="text-xs text-[#5C5F5C] mt-1">
              Upload MRI or CT images for AI-powered analysis
            </p>
          </div>
        )}

        {/* Image Annotator Modal */}
        {annotatingImage && imageBlobs[annotatingImage.id] && (
          <ImageAnnotator
            imageUrl={imageBlobs[annotatingImage.id]}
            imageId={annotatingImage.id}
            initialAnnotations={annotatingImage.annotations || []}
            isOpen={!!annotatingImage}
            onClose={() => setAnnotatingImage(null)}
            onSave={handleSaveAnnotations}
          />
        )}

        {/* Image Comparison Modal */}
        <ImageComparison
          images={images}
          imageBlobs={imageBlobs}
          isOpen={showComparison}
          onClose={() => setShowComparison(false)}
        />
      </CardContent>
    </Card>
  );
};

export default ImageUploader;
